import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ReadAloudButton } from "@/components/ReadAloudButton";
import { LessonFlashcardsButton } from "@/components/LessonFlashcards";
import { ArrowLeft, ArrowRight, BookOpen, HelpCircle, Send, Sparkles, X, CheckCircle2, Trophy, Cross, Cloud, Loader2, RotateCw, Youtube, Copy, ClipboardPaste, ShieldCheck, FileText } from "lucide-react";
const recordWatch = (assignmentId, v) => {
  api.post("/video/watched", {
    assignment_id: assignmentId, video_id: v.video_id, title: v.title,
    channel: v.channel, channel_id: v.channel_id, url: v.url,
  }).catch(() => {});
};

export default function LessonPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const homePath = user?.role === "Student" ? "/student" : "/parent/continuing-education";
  const [a, setA] = useState(null);
  const [stage, setStage] = useState("read"); // read | answer | result
  const [answers, setAnswers] = useState({});
  const [qi, setQi] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [resumed, setResumed] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);
  const loaded = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const [ar, dr] = await Promise.all([
        api.get(`/assignments/${id}`),
        api.get(`/drafts/${id}`).catch(() => ({ data: null })),
      ]);
      if (!active) return;
      setA(ar.data);
      const draft = dr.data;
      const hasProgress = draft && (Object.keys(draft.answers || {}).length > 0 || draft.current_question > 0);
      if (ar.data.status !== "Graded" && hasProgress) {
        const qcount = (ar.data.lesson?.questions || []).length;
        setAnswers(draft.answers || {});
        setQi(Math.min(draft.current_question || 0, Math.max(0, qcount - 1)));
        setStage("answer");
        setResumed(true);
        setSaveState("saved");
      }
      loaded.current = true;
    })();
    return () => { active = false; };
  }, [id]);

  // Debounced auto-save (silent) while answering, so progress is never lost.
  useEffect(() => {
    if (!loaded.current || !a || a.status === "Graded" || stage !== "answer") return;
    setSaveState("saving");
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/drafts/${id}`, { answers, current_question: qi });
        setSaveState("saved");
        setDirty(false);
      } catch { setSaveState("idle"); }
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [answers, qi, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before closing/refreshing the tab if a keystroke hasn't saved yet.
  useEffect(() => {
    const handler = (e) => {
      if (dirty && stage === "answer" && a && a.status !== "Graded") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, stage, a]);

  // Immediately persist the latest edits before leaving the lesson in-app.
  const flushSave = async () => {
    clearTimeout(saveTimer.current);
    if (!loaded.current || !a || a.status === "Graded" || stage !== "answer" || !dirty) return;
    try {
      await api.put(`/drafts/${id}`, { answers, current_question: qi });
      setSaveState("saved");
      setDirty(false);
    } catch (e) { console.debug("draft autosave failed (kept dirty; beforeunload will guard):", e); }
  };

  const goDashboard = async () => { await flushSave(); navigate(homePath); };

  const [videoHelp, setVideoHelp] = useState(null); // null | {loading} | {query, videos} | {error}
  const openVideoHelp = async (question = "") => {
    setVideoHelp({ loading: true });
    try {
      const { data } = await api.post("/tutor/videos", { assignment_id: id, question });
      setVideoHelp({ query: data.query, videos: data.videos || [], limited: data.limited });
    } catch {
      setVideoHelp({ error: true });
    }
  };

  const copyAnswer = async (qid) => {
    try { await navigator.clipboard.writeText(answers[qid] || ""); toast.success("Answer copied to clipboard"); }
    catch { toast.error("Couldn't copy — try selecting the text and pressing Ctrl+C"); }
  };
  const pasteAnswer = async (qid) => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return toast.info("Your clipboard is empty");
      setAnswers((a) => ({ ...a, [qid]: `${a[qid] || ""}${text}` }));
      toast.success("Pasted from clipboard");
    } catch { toast.error("Couldn't read clipboard — click in the box and press Ctrl+V"); }
  };

  if (!a) return <div><TopBar subtitle="Lesson" /><p className="p-10 text-[#6B7280]">Loading…</p></div>;
  const lesson = a.lesson || {};
  const questions = lesson.questions || [];

  const submit = async () => {
    clearTimeout(saveTimer.current);
    setDirty(false);
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions", { assignment_id: id, answers });
      setResult(data); setStage("result");
      toast.success(`Submitted! You scored ${data.percentage}%`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const markComplete = async () => {
    setSubmitting(true);
    try {
      await api.post("/submissions", { assignment_id: id, answers: {} });
      toast.success("Marked complete — great work!");
      navigate(homePath);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const token = localStorage.getItem("rfa_token");
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${token}`;
  const hasQuestions = questions.length > 0;

  return (
    <div className="pb-20">
      <TopBar subtitle={a.subject} />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <button onClick={goDashboard} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Dashboard</button>

        {stage === "read" && (
          <article>
            <span className="text-xs font-semibold uppercase tracking-wide text-[#81B29A]">{a.subject}</span>
            <h1 className="font-head text-4xl font-bold text-[#3D405B] mt-1">{lesson.chapter_title || a.title}</h1>
            <div className="mt-4">
              <ReadAloudButton testid="lesson-read-aloud" label="Read lesson aloud"
                text={`${lesson.chapter_title || a.title}. ${(lesson.learning_objectives || []).join(". ")}. ${lesson.summary || ""} Key facts: ${(lesson.key_facts || []).join(". ")}`} />
            </div>
            <div className="mt-3"><LessonFlashcardsButton lesson={lesson} /></div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="video-help-btn" onClick={() => openVideoHelp("")}
                className="flex items-center gap-2 rounded-full bg-[#E07A5F]/10 border border-[#E07A5F]/40 text-[#E07A5F] px-4 py-2 text-sm font-medium hover:bg-[#E07A5F]/20 transition-colors">
                <Youtube size={16} /> Get video help on this topic
              </button>
              <button data-testid="study-guide-btn" onClick={() => navigate(`${user?.role === "Student" ? "/student" : "/parent"}/study-guide/${id}`)}
                className="flex items-center gap-2 rounded-full bg-[#81B29A]/10 border border-[#81B29A]/40 text-[#5f8c76] px-4 py-2 text-sm font-medium hover:bg-[#81B29A]/20 transition-colors">
                <FileText size={16} /> Chapter study guide
              </button>
            </div>

            {lesson.learning_objectives?.length > 0 && (
              <Section title="What you're going to learn">
                <ul className="list-disc pl-5 space-y-1 text-[#3D405B]">{lesson.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
              </Section>
            )}

            <Section title="Chapter summary">
              <p className="leading-relaxed text-[#3D405B] whitespace-pre-line">{lesson.summary}</p>
            </Section>

            {lesson.attachments?.length > 0 && (
              <div className="mt-6 space-y-5" data-testid="lesson-attachments">
                {lesson.attachments.map((att, i) => (
                  <div key={i} data-testid={`lesson-attachment-${i}`}>
                    {att.kind === "image" ? (
                      <figure className="rounded-2xl overflow-hidden border border-[#EAE6DF] bg-white">
                        <img src={fileUrl(att.path)} alt={att.name} className="w-full object-contain" loading="lazy" />
                        <figcaption className="px-4 py-2 text-xs text-[#6B7280] border-t border-[#EAE6DF] flex items-center gap-1.5"><FileText size={13} /> {att.name}</figcaption>
                      </figure>
                    ) : att.kind === "pdf" ? (
                      <div className="rounded-2xl overflow-hidden border border-[#EAE6DF] bg-white">
                        <iframe src={fileUrl(att.path)} title={att.name} className="w-full h-[70vh]" />
                        <div className="px-4 py-2 text-xs text-[#6B7280] border-t border-[#EAE6DF] flex items-center justify-between">
                          <span className="flex items-center gap-1.5"><FileText size={13} /> {att.name}</span>
                          <a href={fileUrl(att.path)} target="_blank" rel="noopener noreferrer" className="text-[#E07A5F] hover:underline">Open in new tab</a>
                        </div>
                      </div>
                    ) : (
                      <a href={fileUrl(att.path)} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-2xl border border-[#EAE6DF] bg-white p-4 hover:border-[#E07A5F] transition-colors">
                        <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#81B29A]/15 text-[#5f8c76]"><FileText size={18} /></span>
                        <span className="text-sm font-medium text-[#3D405B]">Open {att.name}</span>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {lesson.images?.length > 0 && (
              <div className="mt-6 space-y-5" data-testid="lesson-images">
                {lesson.images.map((img, i) => (
                  <figure key={i} className="rounded-2xl overflow-hidden border border-[#EAE6DF] bg-white" data-testid={`lesson-image-${i}`}>
                    <img src={img.url} alt={img.caption || "Lesson image"} className="w-full object-cover" loading="lazy" />
                    {img.caption && (
                      <figcaption className="px-4 py-3 text-sm text-[#6B7280] italic border-t border-[#EAE6DF]">{img.caption}</figcaption>
                    )}
                  </figure>
                ))}
              </div>
            )}

            {lesson.key_facts?.length > 0 && (
              <Section title="Key Facts">
                <ul className="space-y-2">
                  {lesson.key_facts.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 rounded-xl bg-[#F4A261]/10 border border-[#F4A261]/30 p-3">
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#F4A261]" />
                      <span className="text-[#3D405B]">{f}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {lesson.vocabulary?.length > 0 && (
              <Section title="Important vocabulary">
                <div className="grid sm:grid-cols-2 gap-3">
                  {lesson.vocabulary.map((v, i) => (
                    <div key={i} className="rounded-xl bg-white border border-[#EAE6DF] p-4">
                      <p className="font-semibold text-[#3D405B]">{v.term}</p>
                      <p className="text-sm text-[#6B7280]">{v.definition}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {lesson.things_to_remember?.length > 0 && (
              <Section title="What you need to remember">
                <ul className="list-disc pl-5 space-y-1 text-[#3D405B]">{lesson.things_to_remember.map((o, i) => <li key={i}>{o}</li>)}</ul>
              </Section>
            )}

            {lesson.faith_connection?.reflection && (
              <section className="mt-8 rounded-2xl p-6 text-white grain relative overflow-hidden" style={{ background: "#3D405B" }}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70 flex items-center gap-2"><Cross size={14} /> Faith Connection</p>
                <p className="mt-2 leading-relaxed text-white/95">{lesson.faith_connection.reflection}</p>
                {lesson.faith_connection.verse && (
                  <p className="mt-3 italic text-white/90">"{lesson.faith_connection.verse}" <span className="stat not-italic text-white/70">— {lesson.faith_connection.reference}</span></p>
                )}
              </section>
            )}

            {a.status === "Graded" ? (
              <div className="mt-10 rounded-2xl bg-[#F2EFE9] border border-[#EAE6DF] p-6 text-center">
                <p className="text-[#6B7280]">You've already completed this assignment.</p>
              </div>
            ) : hasQuestions ? (
              <button data-testid="begin-assignment-btn" onClick={() => setStage("answer")}
                className="mt-10 flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3.5 font-semibold hover:bg-[#6f9d87] transition-colors">
                Begin assignment <ArrowRight size={18} />
              </button>
            ) : (
              <button data-testid="mark-complete-btn" onClick={markComplete} disabled={submitting}
                className="mt-10 flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3.5 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60">
                <CheckCircle2 size={18} /> {submitting ? "Saving…" : "Mark complete"}
              </button>
            )}
          </article>
        )}

        {stage === "answer" && (
          <div>
            {resumed && (
              <motion.div data-testid="resume-banner" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                className="mb-5 flex items-center gap-2 rounded-2xl border border-[#81B29A]/40 bg-[#81B29A]/10 px-4 py-3 text-sm text-[#3D405B]">
                <RotateCw size={15} className="text-[#81B29A]" /> Welcome back! We picked up right where you left off.
              </motion.div>
            )}
            <div className="flex items-center justify-between mb-6">
              <span className="stat text-sm text-[#6B7280]">Question {qi + 1} of {questions.length}</span>
              <div className="flex-1 mx-4 h-1.5 bg-[#EAE6DF] rounded-full overflow-hidden">
                <div className="h-full bg-[#81B29A] transition-all" style={{ width: `${((qi + 1) / questions.length) * 100}%` }} />
              </div>
              <span data-testid="save-indicator" className="flex items-center gap-1.5 text-xs text-[#6B7280] whitespace-nowrap">
                {saveState === "saving" ? (<><Loader2 size={13} className="animate-spin" /> Saving…</>)
                  : saveState === "saved" ? (<><Cloud size={13} className="text-[#81B29A]" /> Progress saved</>)
                  : null}
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={qi} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                className="rounded-2xl bg-white border border-[#EAE6DF] p-8">
                <p className="font-head text-xl font-semibold text-[#3D405B]">{questions[qi].question}</p>
                {(questions[qi].type === "multiple_choice" || questions[qi].type === "true_false") ? (
                  <div className="mt-6 space-y-3">
                    {(questions[qi].options || ["True", "False"]).map((opt, i) => (
                      <button key={i} data-testid={`option-${qi}-${i}`}
                        onClick={() => setAnswers({ ...answers, [questions[qi].id]: opt })}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${answers[questions[qi].id] === opt ? "border-[#81B29A] bg-[#81B29A]/10 text-[#3D405B]" : "border-[#EAE6DF] hover:border-[#81B29A]"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <button data-testid={`paste-answer-${qi}`} onClick={() => pasteAnswer(questions[qi].id)}
                        className="flex items-center gap-1.5 text-xs rounded-full border border-[#EAE6DF] px-3 py-1.5 text-[#6B7280] hover:border-[#81B29A] hover:text-[#81B29A] transition-colors"><ClipboardPaste size={13} /> Paste</button>
                      <button data-testid={`copy-answer-${qi}`} onClick={() => copyAnswer(questions[qi].id)}
                        className="flex items-center gap-1.5 text-xs rounded-full border border-[#EAE6DF] px-3 py-1.5 text-[#6B7280] hover:border-[#81B29A] hover:text-[#81B29A] transition-colors"><Copy size={13} /> Copy</button>
                      <span className="text-[11px] text-[#9CA3AF] ml-auto">Copy &amp; paste works here (Ctrl/Cmd + C/V)</span>
                    </div>
                    <textarea data-testid={`answer-${qi}`} value={answers[questions[qi].id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [questions[qi].id]: e.target.value })}
                      className="w-full min-h-[140px] rounded-xl border border-[#EAE6DF] p-4 outline-none focus:ring-2 focus:ring-[#81B29A] resize-y" placeholder="Type your answer here… (you can paste from a document too)" />
                  </div>
                )}
                <div className="mt-4">
                  <button data-testid="video-help-answer-btn" onClick={() => openVideoHelp(questions[qi].question)}
                    className="flex items-center gap-2 text-sm text-[#E07A5F] hover:underline">
                    <Youtube size={15} /> Get video help on this topic
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-between mt-6">
              <button disabled={qi === 0} onClick={() => setQi(qi - 1)} className="rounded-full border border-[#EAE6DF] px-6 py-2.5 disabled:opacity-40 hover:border-[#3D405B] transition-colors">Back</button>
              {qi < questions.length - 1 ? (
                <button data-testid="next-question-btn" onClick={() => setQi(qi + 1)} className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-6 py-2.5 font-semibold hover:bg-[#6f9d87] transition-colors">Next <ArrowRight size={16} /></button>
              ) : (
                <button data-testid="submit-assignment-btn" onClick={submit} disabled={submitting} className="flex items-center gap-2 rounded-full bg-[#E07A5F] text-white px-8 py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
                  <Send size={16} /> {submitting ? "Grading…" : "Submit assignment"}
                </button>
              )}
            </div>
          </div>
        )}

        {stage === "result" && result && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="result-panel" className="text-center">
            <div className="inline-grid place-items-center w-20 h-20 rounded-full bg-[#81B29A] text-white mb-4"><Trophy size={34} /></div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">You scored <span className="stat">{result.percentage}%</span></h1>
            <p className="text-[#6B7280] mt-1">{result.points_earned} / {result.max_points} points · Grade {result.letter_grade}</p>
            <div className="mt-8 space-y-3 text-left">
              {result.graded.map((g, i) => (
                <div key={i} className="rounded-xl bg-white border border-[#EAE6DF] p-4">
                  <div className="flex justify-between gap-3">
                    <p className="text-sm font-medium text-[#3D405B]">{i + 1}. {g.question}</p>
                    <span className="stat text-sm whitespace-nowrap" style={{ color: g.score === g.max_points ? "#81B29A" : "#F4A261" }}>{g.score}/{g.max_points}</span>
                  </div>
                  <p className="mt-2 text-sm text-[#6B7280] bg-[#F2EFE9] rounded-lg p-2 flex items-start gap-1"><CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[#81B29A]" /> {g.feedback}</p>
                </div>
              ))}
            </div>
            <button onClick={() => navigate(homePath)} className="mt-8 rounded-full bg-[#81B29A] text-white px-8 py-3 font-semibold hover:bg-[#6f9d87] transition-colors">Back to dashboard</button>
          </motion.div>
        )}
      </main>

      {stage !== "result" && (
        <button data-testid="tutor-toggle" onClick={() => setTutorOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-5 py-3 shadow-lg hover:scale-105 transition-transform">
          <HelpCircle size={18} /> I don't understand this
        </button>
      )}
      <AnimatePresence>
        {tutorOpen && <TutorPanel assignmentId={id} readingLevel={a.reading_level} onClose={() => setTutorOpen(false)} />}
      </AnimatePresence>
      {videoHelp && <VideoHelpModal state={videoHelp} assignmentId={id} onClose={() => setVideoHelp(null)} onRetry={() => openVideoHelp("")} />}
    </div>
  );
}

function VideoHelpModal({ state, assignmentId, onClose, onRetry }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        data-testid="video-help-modal" className="w-full max-w-lg rounded-3xl bg-white p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="flex items-center gap-2 font-head font-semibold text-[#3D405B]"><Youtube size={18} className="text-[#E07A5F]" /> Video help</span>
          <button data-testid="video-help-close" onClick={onClose} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
        </div>
        {state.loading ? (
          <div className="py-10 text-center text-[#6B7280] flex flex-col items-center gap-2"><Loader2 className="animate-spin" /> Finding safe videos for you…</div>
        ) : state.error ? (
          <div className="py-8 text-center">
            <p className="text-[#6B7280]">I couldn't load videos right now.</p>
            <button data-testid="video-help-retry" onClick={onRetry} className="mt-3 rounded-full bg-[#81B29A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#6f9d87] transition-colors">Try again</button>
          </div>
        ) : !state.videos?.length ? (
          <p className="py-8 text-center text-[#6B7280]">{state.limited ? "Your parent limits videos to approved channels, and none matched this topic yet. Ask the tutor to explain it instead!" : "No good videos found for this topic. Try asking the tutor to explain it!"}</p>
        ) : (
          <>
            <p className="text-sm text-[#6B7280] mb-3">Safe, kid-friendly videos about <b className="text-[#3D405B]">"{state.query}"</b>. Tap one to watch in a new tab.</p>
            {state.limited && (
              <p data-testid="video-limited-note" className="text-xs text-[#6B7280] bg-[#F2EFE9] rounded-xl px-3 py-2 mb-3 flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#81B29A]" /> Some videos are limited by your parent.</p>
            )}
            <div className="space-y-2">
              {state.videos.map((v) => (
                <a key={v.video_id} data-testid={`video-help-item-${v.video_id}`} href={v.url} target="_blank" rel="noopener noreferrer"
                  onClick={() => recordWatch(assignmentId, v)}
                  className="flex gap-3 items-center rounded-2xl bg-white border border-[#EAE6DF] p-2 hover:border-[#E07A5F] hover:shadow-sm transition-all group">
                  <div className="relative shrink-0">
                    <img src={v.thumbnail} alt="" className="w-32 h-20 object-cover rounded-xl" />
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="grid place-items-center w-9 h-9 rounded-full bg-[#E07A5F]/90 text-white group-hover:scale-110 transition-transform"><Youtube size={18} /></span>
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#3D405B] leading-snug line-clamp-2">{v.title}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5 truncate">{v.channel}</p>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

function Section({ title, children }) {
  return (<section className="mt-8"><h2 className="font-head text-lg font-semibold text-[#3D405B] mb-3">{title}</h2>{children}</section>);
}

const MODES = [
  { key: "explain_differently", label: "Explain differently" },
  { key: "example", label: "Give an example" },
  { key: "easier", label: "Make it easier" },
  { key: "quiz", label: "Quiz me" },
];

function TutorPanel({ assignmentId, onClose }) {
  const [msgs, setMsgs] = useState([{ from: "tutor", text: "Hi! What part don't you understand? I'll help you figure it out — but I won't just hand you the answers 😊" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);

  const ask = async (question, mode = "ask") => {
    if (!question.trim()) return;
    setMsgs((m) => [...m, { from: "me", text: question }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/tutor", { assignment_id: assignmentId, question, mode });
      setMsgs((m) => [...m, { from: "tutor", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { from: "tutor", text: "Sorry, I had trouble. Try asking again." }]);
    } finally { setBusy(false); }
  };

  const findVideos = async () => {
    if (videoBusy) return;
    const lastQ = [...msgs].reverse().find((m) => m.from === "me")?.text || "";
    setMsgs((m) => [...m, { from: "me", text: "Find a video that explains this" }]);
    setVideoBusy(true);
    try {
      const { data } = await api.post("/tutor/videos", { assignment_id: assignmentId, question: lastQ });
      if (!data.videos?.length) {
        setMsgs((m) => [...m, { from: "tutor", text: data.limited ? "Your parent limits videos to approved channels, and none matched this yet. I can explain it instead!" : "I couldn't find a good video for this one. Try asking me to explain it instead!" }]);
      } else {
        const note = data.limited ? ' (Some videos are limited by your parent.)' : "";
        setMsgs((m) => [...m, { from: "tutor", text: `Here are some safe videos about "${data.query}" you can watch:${note}`, videos: data.videos }]);
      }
    } catch (e) {
      setMsgs((m) => [...m, { from: "tutor", text: "I couldn't reach the videos right now. Try again in a moment." }]);
    } finally { setVideoBusy(false); }
  };

  return (
    <motion.div initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: "spring", damping: 26 }}
      className="fixed top-0 right-0 z-50 h-full w-full max-w-md backdrop-blur-xl bg-white/85 border-l border-[#EAE6DF] flex flex-col">
      <div className="p-5 border-b border-[#EAE6DF] flex items-center justify-between">
        <span className="flex items-center gap-2 font-head font-semibold text-[#3D405B]"><Sparkles size={18} className="text-[#81B29A]" /> Your Tutor</span>
        <button data-testid="tutor-close" onClick={onClose} className="text-[#6B7280] hover:text-[#3D405B]"><X /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={m.from === "me" ? "flex justify-end" : ""}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.from === "me" ? "bg-[#81B29A] text-white" : "bg-white border border-[#EAE6DF] text-[#3D405B]"}`}>{m.text}</div>
            {m.videos && (
              <div data-testid="tutor-videos" className="mt-2 space-y-2">
                {m.videos.map((v) => (
                  <a key={v.video_id} data-testid={`tutor-video-${v.video_id}`} href={v.url} target="_blank" rel="noopener noreferrer"
                    onClick={() => recordWatch(assignmentId, v)}
                    className="flex gap-3 items-center rounded-2xl bg-white border border-[#EAE6DF] p-2 hover:border-[#E07A5F] hover:shadow-sm transition-all group">
                    <div className="relative shrink-0">
                      <img src={v.thumbnail} alt="" className="w-28 h-16 object-cover rounded-xl" />
                      <span className="absolute inset-0 grid place-items-center">
                        <span className="grid place-items-center w-8 h-8 rounded-full bg-[#E07A5F]/90 text-white group-hover:scale-110 transition-transform"><Youtube size={16} /></span>
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#3D405B] leading-snug line-clamp-2">{v.title}</p>
                      <p className="text-xs text-[#6B7280] mt-0.5 truncate">{v.channel}</p>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {(busy || videoBusy) && <div className="text-sm text-[#6B7280]">{videoBusy ? "Finding safe videos…" : "Thinking…"}</div>}
      </div>
      <div className="p-4 border-t border-[#EAE6DF]">
        <div className="flex flex-wrap gap-2 mb-3">
          {MODES.map((m) => (
            <button key={m.key} data-testid={`tutor-mode-${m.key}`} onClick={() => ask(m.label, m.key)} disabled={busy}
              className="text-xs px-3 py-1.5 rounded-full bg-[#F2EFE9] border border-[#EAE6DF] hover:border-[#81B29A] transition-colors">{m.label}</button>
          ))}
          <button data-testid="tutor-find-video" onClick={findVideos} disabled={videoBusy}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#E07A5F]/10 border border-[#E07A5F]/40 text-[#E07A5F] hover:bg-[#E07A5F]/20 transition-colors disabled:opacity-60"><Youtube size={13} /> Find a video</button>
        </div>
        <div className="flex gap-2">
          <input data-testid="tutor-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask(input)}
            className="flex-1 rounded-full border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#81B29A]" placeholder="Ask your question…" />
          <button data-testid="tutor-send" onClick={() => ask(input)} disabled={busy} className="grid place-items-center w-11 h-11 rounded-full bg-[#81B29A] text-white hover:bg-[#6f9d87] transition-colors"><Send size={16} /></button>
        </div>
      </div>
    </motion.div>
  );
}
